import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { PointStyleApiModel, PointStyleRelationship, WithTemplates } from '../../../types/models/point-style.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchPointStyles } from '../api/point-styles.api';
import { useDeletePointStyle } from '../hooks/api/use-point-styles.hook';

export const PointStyles = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deletePointStyle, { loading: deletingPointStyle, error: deletePointStyleError }] = useDeletePointStyle();

  const columns: ColDef<PointStyleApiModel & WithTemplates>[] = [
    {
      field: 'name',
      valueGetter: ({ data }) => data?.name,
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'templates',
      headerName: 'Template(s)',
      valueGetter: ({ data }) => data?.templates?.map(({ name }) => name).join(', ') ?? '',
    },
    {
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<PointStyleApiModel & WithTemplates>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deletePointStyle({ pointStyleId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Point Style',
                message: `Are you sure you want to delete point style "${node.data?.name}"?`,
                error: deletePointStyleError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getPointStyles: Parameters<typeof InfniteScrollingGrid<PointStyleApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchPointStyles({
      name: filterQuery?.name,
      include: [PointStyleRelationship.TEMPLATES],
      cursor,
      limit,
      sort: sortQuery,
    });

    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title="Civil 3D Point Styles" />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Civil 3D Point Styles</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Point Style
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid loading={deletingPointStyle} columnDefs={columns} onGetRows={getPointStyles} />
        </Box>
      </Box>
    </Box>
  );
};
